/**
 * @author jakubmaslon
 * @since 2020-07-31
 */

import * as React from "react";
import { useHistory } from "react-router";
import { useDispatch } from "react-redux";

import { ROUTES } from "@config/app.routing";
import { BOTTOM_NAVIGATION_SPACE_KEEPER_HEIGHT } from "@config/consts";
import { CLIENT_DASHBOARD_MOBILE } from "@config/testIds/clientDashboard";

import { ThunkActionDispatch } from "@typings/reduxThunkActions";

import { TestWrapper } from "@ui/Atoms";
import {
  BroomCircle,
  HeadphonesMicrophone,
  ListParagraphDotsCheckmark,
  Setting,
  UserProfileSquare,
} from "@ui/Assets/MyIcons";

import { AsyncActionResult } from "@services/AsyncActionCreatorFactory";

import { useAuth, useAuthManager } from "@hooks/useAuth";
import { useScrollPosition, SCROLL_DIR } from "@hooks/useScrollPosition";
import { useTranslate } from "@hooks/useTranslate";
import { useFeatureToggle } from "@hooks/useFeatureToggle";

import { fetchServiceLocationCoverage } from "@app/redux/serviceLocation/serviceLocation.actions";

import {
  disabledIconStyle,
  inactiveIconStyle,
  MY_CLEANINGS_ROUTE,
  SUPPORT_ROUTE,
  PROFILE_ROUTE,
  OPTIONS_ROUTE,
  ACTIVE_BOOKING_BTN_ROUTES,
  ACTIVE_MY_CLEANINGS_BTN_ROUTES,
  getIconStyle,
  ROUTES_WITHOUT_BOTTOM_NAVIGATION,
  ROUTES_WITH_PARAMS_WITHOUT_BOTTOM_NAVIGATION,
} from "./BottomNavigation.helpers";
import {
  FixedNavigationBarSpaceKeeper,
  FixedNavigationBar,
  NavItem,
  NavLinkStyled,
  LinkStyled,
  Label,
  Icon,
} from "./BottomNavigation.styles";

const BottomNavigation = (): React.ReactElement | null => {
  const translate = useTranslate("batmaid_pwa_generic");
  const { openLoginModal, disallowTemporaryLogin, setOnSuccessCallback } =
    useAuthManager();

  const {
    location: { pathname },
  } = useHistory();

  const { isUserLoggedIn, userZipCode } = useAuth();

  const dispatch = useDispatch<ThunkActionDispatch>();

  const [serviceLocationId, setServiceLocationId] = React.useState<
    string | null
  >(null);

  const { scrollDir, currPos } = useScrollPosition();

  React.useEffect(() => {
    if (userZipCode && !serviceLocationId) {
      (async () => {
        const check = await dispatch<Promise<AsyncActionResult>>(
          fetchServiceLocationCoverage(userZipCode),
        );
        const data = await check.payload?.data;

        if (data) {
          setServiceLocationId(await data.serviceLocationId);
        }
      })();
    }
  }, [userZipCode]);

  const getSpaceLeftUnderScroll = React.useCallback(() => {
    const documentHeight = document.body.scrollHeight;
    const scrollBottomPosition = window.innerHeight + Math.abs(currPos.y);
    const spaceLeftUnderhScroll = documentHeight - scrollBottomPosition;

    return spaceLeftUnderhScroll;
  }, [currPos.y]);

  const getBookingRoute = (): string => {
    if (userZipCode) {
      return `${ROUTES.BOOKING}?postalCode=${userZipCode}`;
    }

    return ROUTES.BASE;
  };

  // nav routes
  const BOOKING_ROUTE = getBookingRoute();

  // is route active consts
  const isBookingRouteActive = ACTIVE_BOOKING_BTN_ROUTES.includes(pathname);
  const isMyCleaningsRouteActive =
    ACTIVE_MY_CLEANINGS_BTN_ROUTES.includes(pathname);
  const isSupportRouteActive = pathname === SUPPORT_ROUTE;
  const isProfileRouteActive = pathname === PROFILE_ROUTE;
  const isOptionsRouteActive = pathname.includes(OPTIONS_ROUTE);

  const handleLoginClick = (e: React.SyntheticEvent, route?: string) => {
    e.preventDefault();
    disallowTemporaryLogin();
    openLoginModal();
    setOnSuccessCallback(() => {
      if (route) {
        window.location.href = route;
      }
    });
  };

  const isRouteWithoutBottomNavigation =
    ROUTES_WITHOUT_BOTTOM_NAVIGATION.includes(pathname);
  const isRouteWithParamsWithoutBottomNavigation =
    ROUTES_WITH_PARAMS_WITHOUT_BOTTOM_NAVIGATION.some(route =>
      pathname.includes(route),
    );

  const isFixedNavigationBarShown =
    scrollDir === SCROLL_DIR.DEFAULT ||
    scrollDir === SCROLL_DIR.UP ||
    (SCROLL_DIR.DOWN &&
      getSpaceLeftUnderScroll() < BOTTOM_NAVIGATION_SPACE_KEEPER_HEIGHT);

  if (
    isRouteWithoutBottomNavigation ||
    isRouteWithParamsWithoutBottomNavigation
  ) {
    return null;
  }

  return (
    <React.Fragment>
      <FixedNavigationBar isShown={isFixedNavigationBarShown} id="bottom-nav">
        {isUserLoggedIn ? (
          <React.Fragment>
            {/* *** BOOKING *** */}
            <NavItem>
              <NavLinkStyled to={BOOKING_ROUTE}>
                <Icon>
                  <BroomCircle {...getIconStyle(isBookingRouteActive)} />
                </Icon>
                <Label isActive={isBookingRouteActive}>
                  {translate("bottom_nav_booking")}
                </Label>
              </NavLinkStyled>
            </NavItem>

            {/* *** MY CLEANINGS *** */}
            <TestWrapper testId={CLIENT_DASHBOARD_MOBILE.MY_CLEANINGS}>
              <NavItem>
                <NavLinkStyled
                  to={`${ROUTES.CLIENT_DASHBOARD}${ROUTES.CLIENT_DASHBOARD_MY_CLEANINGS}`}
                >
                  <Icon>
                    <ListParagraphDotsCheckmark
                      {...getIconStyle(isMyCleaningsRouteActive)}
                    />
                  </Icon>
                  <Label isActive={isMyCleaningsRouteActive}>
                    {translate("bottom_nav_my_cleanings")}
                  </Label>
                </NavLinkStyled>
              </NavItem>
            </TestWrapper>

            {/* *** SUPPORT *** */}
            <NavItem>
              <NavLinkStyled to={ROUTES.SUPPORT}>
                <Icon>
                  <HeadphonesMicrophone
                    {...getIconStyle(isSupportRouteActive)}
                  />
                </Icon>
                <Label isActive={isSupportRouteActive}>
                  {translate("bottom_nav_support")}
                </Label>
              </NavLinkStyled>
            </NavItem>

            {/* *** PROFILE *** */}
            <TestWrapper testId={CLIENT_DASHBOARD_MOBILE.PROFILE}>
              <NavItem>
                <NavLinkStyled
                  to={`${ROUTES.CLIENT_DASHBOARD}${ROUTES.CLIENT_DASHBOARD_PROFILE}`}
                >
                  <Icon>
                    <UserProfileSquare
                      {...getIconStyle(isProfileRouteActive)}
                    />
                  </Icon>
                  <Label isActive={isProfileRouteActive}>
                    {translate("bottom_nav_profile")}
                  </Label>
                </NavLinkStyled>
              </NavItem>
            </TestWrapper>
          </React.Fragment>
        ) : (
          <React.Fragment>
            {/* *** BOOKING *** */}
            <NavItem>
              <NavLinkStyled to={BOOKING_ROUTE}>
                <Icon>
                  <BroomCircle {...getIconStyle(isBookingRouteActive)} />
                </Icon>
                <Label isActive={isBookingRouteActive}>
                  {translate("bottom_nav_booking")}
                </Label>
              </NavLinkStyled>
            </NavItem>

            {/* *** MY CLEANINGS *** */}
            <NavItem>
              <LinkStyled
                href="#"
                onClick={e => handleLoginClick(e, MY_CLEANINGS_ROUTE)}
              >
                <Icon>
                  <ListParagraphDotsCheckmark {...disabledIconStyle} />
                </Icon>
                <Label isDisabled>{translate("bottom_nav_my_cleanings")}</Label>
              </LinkStyled>
            </NavItem>

            {/* *** SUPPORT *** */}
            <NavItem>
              <NavLinkStyled to={ROUTES.SUPPORT}>
                <Icon>
                  <HeadphonesMicrophone
                    {...getIconStyle(isSupportRouteActive)}
                  />
                </Icon>
                <Label isActive={isSupportRouteActive}>
                  {translate("bottom_nav_support")}
                </Label>
              </NavLinkStyled>
            </NavItem>

            {/* *** LOG IN *** */}
            <NavItem>
              <LinkStyled href="#" onClick={e => handleLoginClick(e)}>
                <Icon>
                  <UserProfileSquare {...inactiveIconStyle} />
                </Icon>
                <Label>{translate("bottom_nav_log_in")}</Label>
              </LinkStyled>
            </NavItem>
          </React.Fragment>
        )}
      </FixedNavigationBar>
      <FixedNavigationBarSpaceKeeper
        isShown={isFixedNavigationBarShown}
        id="bottom-nav-spacer"
      />
    </React.Fragment>
  );
};

export { BottomNavigation };
